import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import Background from "@/assets/login-background.svg?react";
import { useFetchPatientAndActiveTreatment } from "@/infra/apis/patients/useFetchPatientAndActiveTreatment";
import { useIntl } from "@/infra/locale";
import { AppRoutes } from "@/infra/router";
import { buildPrescriptionTreatmentSummary } from "@/utils/prescriptionUtils";
import { QueryKeys } from "@/infra/query";
import { useAuth } from "@akina/infra";
import { Button, ExternalLink, Icon, Logo, Modal, Text, UploadPrescriptionModalContent } from "@akina/presentation";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

type GuardType = "physiotherapistDisconnected" | "prescriptionExpired" | "invalidPrescriptionState";

type InvalidPatientStatusGuardProps = Readonly<{
  icon: "search" | "hourglassRounded";
  titleId: `settings.account.${GuardType}.title`;
  descriptionId: `settings.account.${GuardType}.description`;
  contactSupportId: `settings.account.${GuardType}.contactSupport`;
  contactSupportButtonVariant?: "primary" | "secondary";
  secondaryButton?: ReactNode;
}>;

function InvalidPatientStatusGuard({
  icon,
  titleId,
  descriptionId,
  contactSupportId,
  contactSupportButtonVariant = "primary",
  secondaryButton,
}: InvalidPatientStatusGuardProps) {
  const { formatMessage } = useIntl();
  const { logout } = useAuth();

  return (
    <div className="relative flex h-screen w-full flex-col items-center justify-center gap-10 overflow-hidden bg-offwhite p-10">
      <Background className="absolute inset-0 z-10 h-screen max-h-full object-fill" />
      <Logo className="z-20 h-8" />

      <div className="no-scrollbar relative z-20 flex max-w-[548px] flex-col gap-6 overflow-y-auto rounded-3xl bg-white p-10 shadow-2xl">
        <button className="absolute right-10 top-10 text-sm font-medium text-primary-100" onClick={() => logout()}>
          {formatMessage({
            id: "dashboard.navigationBar.profileMenu.logOut",
          })}
        </button>
        <Icon type={icon} className="mt-2 self-center text-gray-800/10" sizeClasses="h-45 w-45" />
        <div className="flex flex-col gap-8">
          <Text size="3xl" className="text-center font-bold">
            {formatMessage({ id: titleId })}
          </Text>
          <Text className="text-center text-gray-800/70">
            {formatMessage({ id: descriptionId })}
          </Text>
          <div className="flex flex-nowrap items-center justify-center gap-8 px-4">
            <ExternalLink
              className="flex shrink-0"
              href={`mailto:${formatMessage({ id: "shared.supportEmail" })}`}>
              <Button variant={contactSupportButtonVariant} className="max-w-fit">
                <Text className="text-center">
                  {formatMessage({ id: contactSupportId })}
                </Text>
              </Button>
            </ExternalLink>
            {secondaryButton}
          </div>
        </div>
      </div>
    </div>
  );
}

export function PhysiotherapistDisconnectedGuard() {
  const guardType = "physiotherapistDisconnected";
  return (
    <InvalidPatientStatusGuard
      icon="search"
      titleId={`settings.account.${guardType}.title`}
      descriptionId={`settings.account.${guardType}.description`}
      contactSupportId={`settings.account.${guardType}.contactSupport`}
    />
  );
}

export function PrescriptionExpiredGuard() {
  const guardType = "prescriptionExpired";
  return (
    <InvalidPatientStatusGuard
      icon="hourglassRounded"
      titleId={`settings.account.${guardType}.title`}
      descriptionId={`settings.account.${guardType}.description`}
      contactSupportId={`settings.account.${guardType}.contactSupport`}
    />
  );
}

export function InvalidPrescriptionStateGuard() {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { patient, activeTreatment } = useFetchPatientAndActiveTreatment();
  const [isUploadPrescriptionOpen, setIsUploadPrescriptionOpen] = useState(false);

  const treatmentSummary =
    patient && activeTreatment ? buildPrescriptionTreatmentSummary(patient, activeTreatment) : undefined;

  const handleOpenUploadPrescription = useCallback(() => {
    setIsUploadPrescriptionOpen(true);
  }, []);

  const handleUploadSuccess = useCallback(async () => {
    await queryClient.refetchQueries({ queryKey: [QueryKeys.fetchActiveTreatment] });
    navigate(AppRoutes.Dashboard, { replace: true });
  }, [queryClient, navigate]);

  return (
    <>
      <InvalidPatientStatusGuard
        icon="hourglassRounded"
        titleId="settings.account.invalidPrescriptionState.title"
        descriptionId="settings.account.invalidPrescriptionState.description"
        contactSupportId="settings.account.invalidPrescriptionState.contactSupport"
        contactSupportButtonVariant="secondary"
        secondaryButton={
          treatmentSummary ? (
            <Button onClick={handleOpenUploadPrescription}>
              {formatMessage({ id: "settings.account.invalidPrescriptionState.uploadButton" })}
            </Button>
          ) : undefined
        }
      />
      {treatmentSummary ? (
        <Modal open={isUploadPrescriptionOpen} onOpenChange={setIsUploadPrescriptionOpen}>
          <UploadPrescriptionModalContent
            formatMessage={formatMessage}
            onOpenChange={setIsUploadPrescriptionOpen}
            treatment={treatmentSummary}
            app="care"
            onUploadSuccess={handleUploadSuccess}
          />
        </Modal>
      ) : null}
    </>
  );
}
